import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  ClipboardCheck,
  Inbox,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Users,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { fetchPendingReviewCount } from '@/lib/adminReviewService';
import { fetchActiveContactInquiryCount } from '@/lib/inquiryService';

type BadgeKey = 'pendingReviews' | 'activeInquiries';

const navItems = [
  {
    to: '/dashboard',
    label: 'ダッシュボード',
    icon: LayoutDashboard,
  },
  {
    to: '/reviews/pending',
    label: '審査管理',
    icon: ClipboardCheck,
    badgeKey: 'pendingReviews',
  },
  {
    to: '/inquiries',
    label: '問い合わせ管理',
    icon: Inbox,
    badgeKey: 'activeInquiries',
  },
  {
    to: '/messages',
    label: 'メッセージ管理',
    icon: MessageSquare,
  },
  {
    to: '/users',
    label: 'ユーザー管理',
    icon: Users,
  },
];

export function AdminLayout() {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [badgeCounts, setBadgeCounts] = useState<Record<BadgeKey, number>>({
    pendingReviews: 0,
    activeInquiries: 0,
  });
  const location = useLocation();
  const adminProfile = useAuthStore((state) => state.adminProfile);
  const signOut = useAuthStore((state) => state.signOut);
  const usesSplitPane = [
    '/reviews/pending',
    '/inquiries',
    '/messages',
    '/users',
  ].some((path) => location.pathname === path);

  useEffect(() => {
    let isMounted = true;

    const loadBadgeCounts = async () => {
      try {
        const [pendingReviews, activeInquiries] = await Promise.all([
          fetchPendingReviewCount(),
          fetchActiveContactInquiryCount(),
        ]);

        if (isMounted) {
          setBadgeCounts({ pendingReviews, activeInquiries });
        }
      } catch (loadError) {
        console.error('Failed to fetch admin navigation badge counts:', loadError);
      }
    };

    void loadBadgeCounts();

    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  const handleSignOut = async () => {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  };

  const renderBadge = (badgeKey?: BadgeKey) => {
    if (!badgeKey) {
      return null;
    }

    const count = badgeCounts[badgeKey];
    if (count <= 0) {
      return null;
    }

    return (
      <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-admin-danger px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-admin-bg md:flex">
      <aside className="hidden h-screen w-64 shrink-0 flex-col border-r border-admin-border bg-white md:sticky md:top-0 md:flex">
        <div className="flex h-16 items-center border-b border-admin-border px-6">
          <span className="text-xl font-heading font-bold tracking-[0.15em] text-admin-ink">
            KULUM
          </span>
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition',
                  isActive
                    ? 'bg-admin-accent/10 text-admin-accent'
                    : 'text-admin-muted hover:bg-admin-surface hover:text-admin-accent',
                ].join(' ')
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              <span className="min-w-0 flex-1">{item.label}</span>
              {renderBadge(item.badgeKey as BadgeKey | undefined)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-admin-border p-4">
          <p className="truncate text-sm font-semibold text-admin-ink">
            {adminProfile?.displayName ?? '管理者'}
          </p>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            disabled={isSigningOut}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-admin-border bg-white px-3 py-2 text-sm font-semibold text-admin-muted transition hover:border-admin-accent hover:text-admin-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            <LogOut className="h-4 w-4" />
            {isSigningOut ? 'ログアウト中...' : 'ログアウト'}
          </button>
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 border-b border-admin-border bg-white/85 backdrop-blur-md md:hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-admin-muted">Admin</p>
              <h1 className="text-lg font-bold tracking-[0.12em] text-admin-ink">KULUM</h1>
            </div>
            <button
              type="button"
              onClick={() => void handleSignOut()}
              disabled={isSigningOut}
              className="rounded-full border border-admin-border bg-white p-2 text-admin-muted transition hover:text-admin-accent disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="ログアウト"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-3 pb-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    'flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold transition',
                    isActive
                      ? 'bg-admin-accent text-white'
                      : 'bg-admin-surface text-admin-muted',
                  ].join(' ')
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
                {renderBadge(item.badgeKey as BadgeKey | undefined)}
              </NavLink>
            ))}
          </nav>
        </header>

        <main
          className={
            usesSplitPane
              ? 'min-w-0 flex-1 bg-admin-surface'
              : 'min-w-0 flex-1 bg-admin-surface px-4 py-5 md:px-8 md:py-8'
          }
        >
          <div className={usesSplitPane ? 'h-full min-w-0' : 'mx-auto max-w-7xl'}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
